// Typed fetch wrapper with automatic access-token refresh.
type ApiOk<T> = { ok: true; data: T };
type ApiErr = { ok: false; error: { code: string; message: string; details?: unknown } };
type ApiResp<T> = ApiOk<T> | ApiErr;

let accessToken: string | null = null;
export const setAccessToken = (t: string | null) => { accessToken = t; };
export const getAccessToken = () => accessToken;

// ── Persistent refresh token (PWA / in-app-webview logout fix) ──
// The server still sets an httpOnly `rt` cookie as the PRIMARY transport,
// but installed PWAs and embedded webviews (Telegram, etc.) routinely
// evict cookies between launches — the root cause of players getting
// logged out on reopen/refresh. localStorage survives those environments,
// so we mirror the refresh token here and replay it via the
// x-refresh-token header. The cookie is still used whenever it's present;
// this is a fallback, not a replacement.
const RT_KEY = 'bg_rt';
function getStoredRefresh(): string | null {
  try { return localStorage.getItem(RT_KEY); } catch { return null; }
}
export function setRefreshToken(t: string | null) {
  try {
    if (t) localStorage.setItem(RT_KEY, t);
    else localStorage.removeItem(RT_KEY);
  } catch { /* private mode / storage disabled — cookie path still works */ }
}

let refreshing: Promise<boolean> | null = null;
async function refresh(): Promise<boolean> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    try {
      const stored = getStoredRefresh();
      const r = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        // Send the stored token so a cookie-less environment can still
        // refresh. Harmless when the cookie is present (server prefers it).
        headers: stored ? { 'x-refresh-token': stored } : undefined,
      });
      const j = (await r.json()) as ApiResp<{ accessToken: string; refreshToken?: string }>;
      if (!r.ok || !j.ok) {
        // A hard 401 means the stored token is dead — drop it so we don't
        // keep replaying a revoked/expired value on every reload.
        if (r.status === 401) setRefreshToken(null);
        return false;
      }
      accessToken = j.data.accessToken;
      if (j.data.refreshToken) setRefreshToken(j.data.refreshToken);
      return true;
    } catch { return false; }
    finally { refreshing = null; }
  })();
  return refreshing;
}

export class ApiError extends Error {
  constructor(public code: string, message: string, public status: number, public details?: unknown) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const headers = new Headers(init.headers || {});
  if (!(init.body instanceof FormData) && init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  if (accessToken) headers.set('authorization', `Bearer ${accessToken}`);
  const res = await fetch(`/api${path}`, { ...init, headers, credentials: 'include' });
  const body = (await res.json().catch(() => ({}))) as ApiResp<T>;
  if (res.status === 401 && retry && accessToken) {
    if (await refresh()) return request<T>(path, init, false);
  }
  if (!res.ok || !('ok' in body) || !body.ok) {
    const err = (body as ApiErr).error || { code: 'http_error', message: `HTTP ${res.status}` };
    throw new ApiError(err.code, err.message, res.status, err.details);
  }
  return body.data;
}

// Logout: revoke the server-side session AND clear the persisted refresh
// token. Sends x-refresh-token so a cookie-less (localStorage-only)
// session still revokes the right server row. Always clears local state
// even if the network call fails.
async function logout(): Promise<void> {
  const stored = getStoredRefresh();
  try {
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
      headers: stored ? { 'x-refresh-token': stored } : undefined,
    });
  } catch { /* clear local state regardless */ }
  accessToken = null;
  setRefreshToken(null);
}

export const api = {
  get: <T>(p: string) => request<T>(p),
  post: <T>(p: string, body?: unknown) =>
    request<T>(p, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(p: string, body?: unknown) =>
    request<T>(p, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  postForm: <T>(p: string, fd: FormData) => request<T>(p, { method: 'POST', body: fd }),
  tryRefresh: refresh,
  logout,
};

export type User = {
  id: number; email: string; username: string; phone: string;
  first_name: string | null; last_name: string | null;
  // Phase D1 (migration 091) — renamed `dob` → `birthdate` to match
  // the agents table. Backend payloads use `birthdate` exclusively
  // from this version forward.
  birthdate: string | null; gender: 'male'|'female'|'other'|'prefer_not' | null;
  // Legacy free-text address fields. After Phase R3 migration 058
  // these come back from the server as ALIASES on the renamed
  // `legacy_*` columns — the data is preserved but the source of
  // truth for new signups is the structured geo block below. Kept
  // here for backward compat with the existing AddressTab read view
  // (so old players still see their typed address). Writes to these
  // fields are REJECTED by the server-side updateProfileSchema.
  address_line1: string | null; address_line2: string | null;
  city: string | null; district: string | null; state: string | null; pincode: string | null; country: string;
  // Phase R3 (Hyper-Local Mesh Network) — structured geo. Filled at
  // signup via LocationCapture, refreshable via Profile "Update
  // location" flow. The locator on the server reads these directly
  // for cascading agent attribution.
  latitude: string | number | null;     // DECIMAL(10,8) — MySQL returns as string
  longitude: string | number | null;    // DECIMAL(11,8) — same
  state_id: number | null;
  district_id: number | null;
  area_id: number | null;
  pincode_id: number | null;
  nation: string | null;
  geo_updated_at: string | null;        // ISO timestamp; null if user never set geo
  language: string;
  secondary_language: string | null;
  profile_image_url: string | null;
  // Task D-2 (2026-05-10): canonical UPI handle the player most
  // recently used in a withdrawal. Pre-fills the next withdrawal
  // form. Always lowercase. NULL until the player completes their
  // first withdrawal.
  saved_upi_id: string | null;
  telegram_chat_id: number | null; telegram_username: string | null;
  telegram_verified_at: string | null; email_verified_at: string | null;
  status: 'pending'|'active'|'suspended'|'banned';
};
